// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Trading.js
// Trading > Portfolio (/trading). For the logged-in user's broker connection: status bar,
// key-figure tiles with today's P&L, allocation by market and by holding (valued in the base
// currency using IBKR's exchange rates), and tabbed tables for positions / open orders / fills /
// order history / the site's order log. Close on a position opens the Trade page prefilled;
// open orders can be modified or cancelled here. Refreshes every 5 seconds.
// Broker: IBKR, through trading.py -> broker_ibkr.py -> ibkr_bridge.py -> IB Gateway.
// Admin-only for now (useTradingAccount guard; API is @admin_required in trading.py).
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import TradingTabs from '../components/TradingTabs';
import OpenOrders from '../components/OpenOrders';
import api from '../api';
import {
  s, REFRESH_MS, isNum, num, signed, tone, arrow, qty, price, chartTicker, errText, nameOf, isOption,
  Table, DataTable, KeyValues, Tile, useTradingAccount, StatusBar, useAccountData, ORDER_COLS, FILL_COLS,
} from '../components/tradingShared';

const CURRENCIES = ['AUD', 'USD'];
const MARKET_NAMES = { US: 'US', AU: 'ASX' };
const TILES = [   // [label, IBKR account tag] - shown in the currency picked on the page
  ['Net liquidation', 'NetLiquidation'], ['Cash', 'TotalCashValue'], ['Buying power', 'BuyingPower'],
  ['Available funds', 'AvailableFunds'], ['Positions value', 'GrossPositionValue'],
];

function useWindowWidth() { const [w, setW] = useState(window.innerWidth); useEffect(() => { const h = () => setW(window.innerWidth); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []); return w; }

// Last price; until IBKR's portfolio feed prices a new position, value / (qty x multiplier).
const lastOf = p => isNum(p.market_price) ? p.market_price
  : isNum(p.market_value) && Number(p.position) ? p.market_value / (p.position * (p.multiplier || 1)) : null;
// Unrealised return %, against what the position cost.
const retOf = p => { const cost = Math.abs(Number(p.position)) * Number(p.avg_cost); return cost ? Number(p.unrealized_pnl) / cost * 100 : null; };

// Phone layout: one card per position (the 10-column table scrolls sideways on a phone).
function PositionCard({ p, label, onClose }) {
  const r = retOf(p);
  const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' };
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={row}>
        <span style={{ fontSize: '14px' }}>{label}</span>
        <span style={{ ...s.mono, fontSize: '16px', fontWeight: 600 }}>{num(p.market_value)} <span style={s.meta}>{p.currency}</span></span>
      </div>
      <div style={{ ...row, ...s.meta, ...s.mono, marginTop: '4px' }}>
        <span>{qty(p.position)} @ {price(p.avg_cost / (p.multiplier || 1))}</span>
        <span>Last {price(lastOf(p))}</span>
      </div>
      <div style={{ ...row, ...s.mono, fontSize: '13px', marginTop: '6px' }}>
        <span style={{ color: tone(p.unrealized_pnl) }}>{signed(p.unrealized_pnl)} ({isNum(r) ? `${signed(r, 1)}%` : '—'})</span>
        <span style={{ color: tone(p.daily_pnl) }}>Today {signed(p.daily_pnl)}</span>
        <span style={s.link} onClick={onClose}>Close</span>
      </div>
    </div>
  );
}

// Horizontal share bars (one hue): label, bar, %, amount.
function Bars({ rows, total, ccy }) {
  if (!rows.length || !total) return <div style={s.meta}>No holdings.</div>;
  return rows.map(r => {
    const pct = r.value / total * 100;
    return (
      <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 56px 110px', gap: '10px', alignItems: 'center', padding: '5px 0', fontSize: '12px' }}>
        <span style={{ ...s.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.label}>{r.label}</span>
        <div style={{ height: '8px', background: 'var(--bg)', borderRadius: '4px' }}>
          <div style={{ width: `${Math.max(pct, 1)}%`, height: '100%', background: 'var(--accent)', borderRadius: '4px' }} />
        </div>
        <span style={{ ...s.mono, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
        <span style={{ ...s.mono, textAlign: 'right', color: 'var(--text-muted)' }}>{num(r.value, 0)} {ccy}</span>
      </div>
    );
  });
}

// Every order this site has sent, from user_broker_orders.
function OrderLog({ broker, tick }) {
  const [rows, setRows] = useState(null);
  const [err, setErr]   = useState('');
  useEffect(() => {
    api.get(`/trading/${broker}/order-log`).then(r => setRows(r.data.orders)).catch(e => setErr(errText(e)));
  }, [broker, tick]);
  return (
    <>
      <div style={{ ...s.meta, padding: '12px 0 4px' }}>Every order woody.wtf has sent to the broker, including rejected ones.</div>
      {err && <div style={s.err}>✕ {err}</div>}
      {rows && <DataTable rows={rows} empty="No orders sent yet." />}
    </>
  );
}

// Tiles, allocation and tabbed tables for one account.
function Account({ broker, acc }) {
  const navigate = useNavigate();
  const phone = useWindowWidth() < 768;
  const [currency, setCurrency] = useState('AUD');
  const [tab, setTab]   = useState('positions');
  const [tick, setTick] = useState(0);         // bumped after an order change, reloads the order log
  const { data, err, updated, reload } = useAccountData(broker, acc, currency);

  const funds = data?.funds?.[0] || {};
  const base  = data?.base_currency || 'AUD';
  const fx    = data?.fx || {};
  const pnl   = data?.pnl;

  // Close a position: open the Trade page with the opposite side and full size.
  const close = p => navigate(`/trading/trade?${new URLSearchParams({
    code: p.code, side: p.position > 0 ? 'SELL' : 'BUY', qty: String(Math.abs(p.position)) })}`);

  // Positions valued in the base currency, for allocation.
  const held = (data?.positions || []).map(p => ({ ...p, base_value: Number(p.market_value || 0) * (fx[p.currency] ?? 1) }));
  const gross = held.reduce((t, p) => t + Math.abs(p.base_value), 0);
  const byMarket = Object.entries(held.reduce((m, p) => {
    const k = isOption(p.code) ? `${p.currency === 'AUD' ? 'ASX' : 'US'} options` : MARKET_NAMES[p.code.split('.')[0]] || p.code.split('.')[0];
    m[k] = (m[k] || 0) + Math.abs(p.base_value); return m;
  }, {})).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  const byHolding = held.map(p => ({ label: isOption(p.code) ? nameOf(p) : p.code, value: Math.abs(p.base_value) }))
    .sort((a, b) => b.value - a.value).slice(0, 8);

  const posCols = [
    { key: 'code', label: 'Code', render: p => isOption(p.code)
        ? <span style={s.link} onClick={() => navigate(`/trading/trade?code=${p.code}`)}>{nameOf(p)}</span>
        : <a style={s.link} href={`/chart?t=${encodeURIComponent(chartTicker(p.code))}`} target="_blank" rel="noopener noreferrer">{p.code}</a> },
    { key: 'position', label: 'Qty', num: true, render: p => qty(p.position) },
    { key: 'avg_cost', label: 'Avg cost', num: true, render: p => price(p.avg_cost / (p.multiplier || 1)) },   // per share / per option
    { key: 'market_price', label: 'Last', num: true, render: p => price(lastOf(p)) },
    { key: 'market_value', label: 'Value', num: true, render: p => num(p.market_value) },
    { key: 'unrealized_pnl', label: 'Unrealised', num: true, render: p => <span style={{ color: tone(p.unrealized_pnl) }}>{signed(p.unrealized_pnl)}</span> },
    { key: 'ret', label: 'Return', num: true, render: p => {
        const r = retOf(p);
        return <span style={{ color: tone(r) }}>{isNum(r) ? `${signed(r, 1)}%` : '—'}</span>;
      } },
    { key: 'daily_pnl', label: 'Today', num: true, render: p => <span style={{ color: tone(p.daily_pnl) }}>{signed(p.daily_pnl)}</span> },
    { key: 'currency', label: 'Ccy' },
  ];

  const tabs = [
    ['positions', 'Positions', data?.positions?.length],
    ['orders', 'Open', data?.orders?.length],
    ['fills', 'Fills', data?.deals?.length],
    ['history', 'History', data?.order_history?.length],
    ['log', 'Log', null],
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <span style={s.meta}>{updated ? `Updated ${updated.toLocaleTimeString('en-GB')} · refreshes every ${REFRESH_MS / 1000}s` : 'Loading…'}</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button style={{ ...s.btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={() => navigate('/trading/trade')}>New order</button>
          <span style={s.meta}>Balances in</span>
          <select style={s.select} value={currency} onChange={e => setCurrency(e.target.value)}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      {err && <div style={{ ...s.err, marginBottom: '12px' }}>✕ {err}</div>}

      <div style={s.tiles}>
        {TILES.map(([label, tag]) => <Tile key={tag} label={label} value={num(funds[tag])} sub={currency} />)}
        <Tile label="Today's P&L" value={`${arrow(pnl?.daily)}${signed(pnl?.daily)}`} color={tone(pnl?.daily)} sub={base} />
        <Tile label="Unrealised P&L" value={`${arrow(pnl?.unrealized)}${signed(pnl?.unrealized)}`} color={tone(pnl?.unrealized)} sub={base} />
        <Tile label="Realised P&L" value={`${arrow(pnl?.realized)}${signed(pnl?.realized)}`} color={tone(pnl?.realized)} sub={`${base}, today`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '20px' }}>
        <div style={s.card}>
          <div style={{ ...s.title, marginBottom: '10px' }}>Allocation by market <span style={s.meta}>· in {base}</span></div>
          <Bars rows={byMarket} total={gross} ccy={base} />
        </div>
        <div style={s.card}>
          <div style={{ ...s.title, marginBottom: '10px' }}>Largest holdings <span style={s.meta}>· in {base}</span></div>
          <Bars rows={byHolding} total={gross} ccy={base} />
        </div>
      </div>

      <div style={s.card}>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
          {tabs.map(([k, label, n]) => (
            <button key={k} style={{ ...s.tab(tab === k), whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setTab(k)}>
              {label}{isNum(n) && <span style={s.count}>{n}</span>}
            </button>
          ))}
        </div>
        {!data && !err && <div style={{ ...s.meta, padding: '16px 0' }}>Loading…</div>}
        {data && tab === 'positions' && (phone
          ? (data.positions.length ? data.positions.map(p => <PositionCard key={p.con_id} p={p} label={posCols[0].render(p)} onClose={() => close(p)} />)
                                   : <div style={{ ...s.meta, padding: '16px 0' }}>No positions.</div>)
          : <Table cols={posCols} rows={data.positions} empty="No positions."
              action={p => <span style={s.link} onClick={() => close(p)}>Close</span>} />)}
        {data && tab === 'orders' && <OpenOrders broker={broker} acc={acc} orders={data.orders}
            onChange={() => { reload(); setTick(t => t + 1); }} />}
        {data && tab === 'fills' && <Table cols={FILL_COLS} rows={data.deals} empty="No fills today." />}
        {data && tab === 'history' && <Table cols={ORDER_COLS} rows={data.order_history} empty="No past orders." />}
        {tab === 'log' && <OrderLog broker={broker} tick={tick} />}
      </div>

      <details style={{ ...s.card, cursor: 'pointer' }}>
        <summary style={s.title}>All account values <span style={s.meta}>· {currency}, as reported by IBKR</span></summary>
        <div style={{ marginTop: '14px', cursor: 'default' }}><KeyValues obj={funds} /></div>
      </details>
    </>
  );
}

export default function Trading() {
  const t = useTradingAccount();
  useEffect(() => { document.title = 'Trading | woody.wtf'; }, []);
  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <h1 style={s.heading}>Trading</h1>
        <TradingTabs />
        <StatusBar t={t} />
        {t.acc && <Account key={t.acc.acc_id} broker={t.broker} acc={t.acc} />}
      </div>
    </div>
  );
}
